import { getDefaultStore } from 'jotai'
import { fetchInstalledSlashCommands, loadInstalledSlashCommandsList } from '@nostr/gadgets/lists'
import { NostrEvent } from '@nostr/tools/core'
import { toast } from 'sonner'
import i18n from 'i18next'
import { currentPubkeyAtom } from './session'
import {
  installedSlashCommandsAtom,
  installSlashCommand,
  parseInstalledSlashCommand,
  replaceInstalledSlashCommands,
  uninstallSlashCommand
} from './slash-commands'
import { getEventHint, publishOpaquely } from '@/services/client.service'
import { parseSlashCommandDefinition } from '@/lib/slash-command-definition'
import { now } from '@/lib/utils'
import { getErrorMessage } from '@/lib/error'

/**
 * Keeps installed slash commands in the user's kind 10992 list (NIP-51), with
 * installedSlashCommandsAtom as the local copy everything else reads from.
 */
export const INSTALLED_COMMANDS_KIND = 10992

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

/** set once any account's list has been mirrored, so local installs are carried over only once */
const MIGRATED_KEY = 's:installedCommandsMigrated'

let syncSerial = 0

/** Mirror the current user's list into the local copy; call on login and account switch. */
export async function syncInstalledSlashCommands() {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  const serial = ++syncSerial
  const stale = () => serial !== syncSerial || store.get(currentPubkeyAtom) !== pubkey

  try {
    const list = await loadInstalledSlashCommandsList(pubkey)
    if (stale()) return

    if (!list.event) {
      const local = store.get(installedSlashCommandsAtom)
      if (!localStorage.getItem(MIGRATED_KEY) && local.length > 0) {
        // commands installed before the list existed become its first entries
        await updateList(pubkey, () => local.map((c) => ['a', `31992:${c.pubkey}:${c.d}`]))
      } else {
        replaceInstalledSlashCommands([])
      }
      localStorage.setItem(MIGRATED_KEY, '1')
      return
    }
    localStorage.setItem(MIGRATED_KEY, '1')

    const fetched = await fetchInstalledSlashCommands(pubkey)
    if (stale()) return

    const local = store.get(installedSlashCommandsAtom)
    const slashCommands = list.items.map((pointer) => {
      const found = fetched.find(
        (c) => c.pubkey === pointer.pubkey && c.d === pointer.identifier
      )
      // when the definition can't be reached right now keep the local copy
      return (
        (found && parseInstalledSlashCommand(found.event)) ??
        local.find((c) => c.pubkey === pointer.pubkey && c.d === pointer.identifier)
      )
    })

    replaceInstalledSlashCommands(slashCommands.filter((c) => c !== undefined))
  } catch (err) {
    console.warn('failed to sync installed commands:', getErrorMessage(err))
  }
}

/** Install a command from its kind 31992 event and add it to the user's list. */
export async function installCommand(event: NostrEvent) {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  const { d } = parseSlashCommandDefinition(event.tags)
  const address = `31992:${event.pubkey}:${d}`

  installSlashCommand(event)
  try {
    await updateList(pubkey, (tags) => {
      const rest = tags.filter((tag) => tag[0] !== 'a' || tag[1] !== address)
      const hint = getEventHint(event.id, event)
      return [...rest, hint ? ['a', address, hint] : ['a', address]]
    })
  } catch (err) {
    uninstallSlashCommand(event.pubkey, d)
    toast.error(t('Failed to install command: {{error}}', { error: getErrorMessage(err) }))
  }
}

/** Uninstall a command and remove it from the user's list. */
export async function uninstallCommand(event: NostrEvent) {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  const { d } = parseSlashCommandDefinition(event.tags)
  const address = `31992:${event.pubkey}:${d}`

  uninstallSlashCommand(event.pubkey, d)
  try {
    await updateList(pubkey, (tags) =>
      tags.filter((tag) => tag[0] !== 'a' || tag[1] !== address)
    )
  } catch (err) {
    installSlashCommand(event)
    toast.error(t('Failed to uninstall command: {{error}}', { error: getErrorMessage(err) }))
  }
}

async function updateList(pubkey: string, modify: (tags: string[][]) => string[][]) {
  const list = await loadInstalledSlashCommandsList(pubkey)
  const event = await publishOpaquely({
    kind: INSTALLED_COMMANDS_KIND,
    content: list.event?.content ?? '',
    tags: modify(list.event?.tags ?? []),
    created_at: now()
  })
  if (!event) throw new Error(t('Failed to publish updated commands list.'))
  await loadInstalledSlashCommandsList(event.pubkey, undefined, event)
}

/** Sync now and again on every login or account switch. */
export function watchInstalledSlashCommands() {
  syncInstalledSlashCommands()
  store.sub(currentPubkeyAtom, () => syncInstalledSlashCommands())
}
